import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Attendance,
  Intervention,
  InterventionGroup,
  InterventionPlanStates,
} from "../../../../../../../../../store/onboarding/cases/types";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { ApplicationState } from "../../../../../../../../../store";
import { toastr } from "react-redux-toastr";
import { useLoading } from "../../../../../../../../../utils/hooks/useLoading";
import DatePicker from "react-datepicker";
import CustomDateInput from "../../../../../../../../common/onboarding/third-step/set-goal-modal/common/CustomDateInput";
import { Form, Spinner } from "react-bootstrap";
import {
  createAttendance,
  updateAttendance,
} from "../../../../../../../../../store/onboarding/cases/actions";
import AttendanceStudentsList from "./AttendanceStudentsList";
import BaseSelect from "react-select";
import { ReactSelectOption } from "../../../../../../../../../store/onboarding/types";
import { ValueType } from "react-select/src/types";
import FixRequiredSelect from "../../../../../../../../common/required-select/FixRequiredSelect";

type OwnProps = {
  show?: boolean;
  onHideForm: () => void;
  selectedAttendance?: Attendance;
  selectedGroup: InterventionGroup;
};

type Props = OwnProps;

const Select = (
  props: JSX.IntrinsicAttributes &
    JSX.IntrinsicClassAttributes<FixRequiredSelect> &
    Readonly<any> &
    Readonly<{ children?: React.ReactNode }>
) => (
  <FixRequiredSelect
    {...props}
    SelectComponent={BaseSelect}
    options={props.options}
    menuShouldBlockScroll={true}
  />
);

const AttendanceUpsertForm: FunctionComponent<Props> = (props) => {
  const { show, onHideForm, selectedAttendance, selectedGroup } = props;
  const dispatch = useDispatch();

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<number[]>([]);
  const [selectedInterventions, setSelectedInterventions] = useState<
    ReactSelectOption<number>[]
  >([]);
  const [interventionError, setInterventionError] = useState<string>("");

  let mergedInterventions:Intervention[] = useMemo(() => {
    let mergedInterventions: Intervention[] = [];
    if(selectedAttendance) {
      mergedInterventions = [...selectedGroup!.interventions, ...selectedGroup!.archived_interventions]
    }
    return Object.values(
      mergedInterventions.reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {})
    );
  },[selectedAttendance])

  const options: ReactSelectOption<number>[] = useMemo(() => {
    let interventionsArray: Intervention[] = [];
      if(selectedAttendance) {
        let activeIntervention = 
          selectedGroup?.interventions
          .filter((intervention) => selectedGroup.intervention_plan?.plan_items?.some((planItem) => planItem.intervention_id == intervention.id && planItem.state == InterventionPlanStates.ACTIVE));
        let mergeWithActiveIntervention: Intervention[] = 
              mergedInterventions.filter((inter:Intervention) => selectedAttendance.interventions.includes(inter.id)).concat(activeIntervention)

        interventionsArray = Object.values(
          mergeWithActiveIntervention.reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {})
        );
      } else {
        interventionsArray = selectedGroup.interventions.filter((intervention) => {
          if(selectedGroup.intervention_plan?.plan_items?.length) {
            return selectedGroup.intervention_plan?.plan_items.some((interventionPlan) => (interventionPlan.intervention_id == intervention.id) && (interventionPlan.state == InterventionPlanStates.ACTIVE))
          } else {
            return intervention;
          }
        });
      }

    return (
      interventionsArray.map((int:Intervention) => ({
        value: int.id,
        label: int.name,
      })) ?? []
    );
  }, [selectedGroup.interventions, selectedAttendance]);

  let allStudent: number[] = useMemo(() => { 
    let studentData: number[] = [];
    selectedGroup.student_goals.forEach((goal) => {
      if (!goal.archived) {
        studentData.push(goal.student.id!);
      }
    });
    return studentData;
  }, [selectedGroup.student_goals]);

  useEffect(() => {
    if (show) {
      if (selectedAttendance) {
        setSelectedDate(moment(selectedAttendance.date).toDate());
        setSelectedStudents(selectedAttendance.present_students);
        setSelectedInterventions(
          selectedAttendance.interventions.map(
            (intId) => options.find((o) => o.value === intId)!
          )
        );
      } else {
        setSelectedDate(new Date());
        setSelectedStudents(allStudent);
        setSelectedInterventions([]);
      }
      setInterventionError("");
    }
  }, [show, selectedAttendance, options, allStudent]);

  const creationLoading = useSelector(
    (s: ApplicationState) => s.cases.isLoading.createAttendance
  );
  const creationError = useSelector(
    (s: ApplicationState) => s.cases.errors.createAttendance
  );
  const onCreationSuccess = useCallback(() => {
    toastr.success("Success", "Attendance is added successfully!");
    onHideForm();
  }, []);
  useLoading(creationLoading, creationError, onCreationSuccess);

  const updateLoading = useSelector(
    (s: ApplicationState) => s.cases.isLoading.updateAttendance
  );
  const updateError = useSelector(
    (s: ApplicationState) => s.cases.errors.updateAttendance
  );
  const onUpdateSuccess = useCallback(() => {
    toastr.success("Success", "Attendance is updated successfully!");
    onHideForm();
  }, []);
  useLoading(updateLoading, updateError, onUpdateSuccess);

  useEffect(() => {
      if(selectedInterventions.length > 0) {
       setInterventionError("");
      }
  }, [selectedInterventions]);

  const validateInterventions = () => {
    if (selectedInterventions.length === 0) {
      setInterventionError("This field is required.");
      return false;
    }
    setInterventionError("");
    return true;
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if(!validateInterventions()) return;
    if (selectedDate) {
      const body: Pick<
        Attendance,
        "date" | "interventions" | "absent_students" | "present_students"
      > = {
        date: selectedDate.toISOString(),
        interventions: selectedInterventions.map((int) => int.value),
        absent_students: allStudent
          .filter((st) => !selectedStudents.includes(st!))
          .map((st) => st!),
        present_students: selectedStudents,
      };

      if (selectedAttendance) {
        dispatch(
          updateAttendance(selectedGroup.id, {
            id: selectedAttendance.id,
            ...body,
          })
        );
      } else {
        dispatch(createAttendance(selectedGroup.id, body));
      }
    }
  };

  const handleSelectedInterventionsIdsChange = (
    values: ValueType<ReactSelectOption<number>, true>
  ) => {
    setSelectedInterventions((values as any) ?? []);
  };

  if (!show) {
    return null;
  }

  return (
    <form className="attendance-upsert-form" onSubmit={handleFormSubmit}>
      <Form.Group className="my-2">
        <Form.Label className="font-weight-bold d-block mb-1">Attendance Date</Form.Label>
        <DatePicker
          required
          customInput={<CustomDateInput />}
          selected={selectedDate}
          onChange={setSelectedDate}
          maxDate={new Date()}
        />
      </Form.Group>  
      <AttendanceStudentsList
        students={selectedGroup.students}
        selectedStudents={selectedStudents}
        setSelectedStudents={setSelectedStudents}
        intervention={selectedGroup}
      />
      <Form.Group className="my-2">
        <Form.Label className="font-weight-bold mb-1">Intervention(s)<span className="label-asterisk">*</span></Form.Label>
        <Select
          menuPlacement="top"
          menuPosition="fixed"
          isMulti
          name="selected-interventions"
          value={selectedInterventions}
          options={options as any}
          onChange={handleSelectedInterventionsIdsChange}
          placeholder="Select"
          styles={{
            control: (base: any, state: any) => ({
              ...base,
              border: interventionError ? "1px solid red" : base.border,
              boxShadow: "none",
              "&:hover": {
                border: interventionError ? "1px solid red" : base.border,
              },
            }),
          }}
        />
        {interventionError && (
          <div style={{ color: "red", fontStyle: "italic", marginTop: "-0.4rem" }}>
            {interventionError}
          </div>
        )}
      </Form.Group>
      <hr />
      <div className="d-flex justify-content-between">
        <button className="whiteBtnSm" onClick={onHideForm}>
          Cancel
        </button>
        <button className="blueBtnSm" type="submit">
          Save{" "}
          {(creationLoading || updateLoading) && (
            <Spinner animation="border" size="sm" className="ml-1" />
          )}
        </button>
      </div>
    </form>
  );
};

export default AttendanceUpsertForm;
